import * as Blockly from 'blockly/core';
import { defineMutableBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import { FieldOperatorDropdown } from '@/(routers)/boards/editor/block/definitions/fields/fieldOperatorDropdown';
import { FieldStyledBadgeLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledBadgeLabel';
import { FieldStyledLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledLabel';
import { FieldVariableLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldVariableLabel';

const COLOR = CATEGORIES.sensing.color;

const POSITION_OPTIONS = {
    orange: [
        { value: 'UPPER_LEFT', label: '左上' },
        { value: 'LOWER_LEFT', label: '左下' },
        { value: 'UPPER_RIGHT', label: '右上' },
        { value: 'LOWER_RIGHT', label: '右下' },
    ],
    blue: [
        { value: 'UPPER_LEFT', label: '左上' },
        { value: 'LOWER_LEFT', label: '左下' },
        { value: 'UPPER_RIGHT', label: '右上' },
        { value: 'LOWER_RIGHT', label: '右下' },
    ],
};

const MODE_OPTIONS = [
    { value: 'PULSE_PER_SECOND', label: '秒間パルス値', tooltip: '今どのくらいの速さで流れているか（流速・瞬間流量）' },
    { value: 'TOTAL_PULSE', label: '累計パルス値', tooltip: '合計でどれだけ流れたか（積算流量）' },
];

const MODE_COLOURS: Record<string, string> = {
    PULSE_PER_SECOND: '#cc0000',
    TOTAL_PULSE: '#00cc00',
};

function buildModeField(mode: string): FieldOperatorDropdown {
    const modeField = new FieldOperatorDropdown(MODE_OPTIONS, mode, undefined, MODE_COLOURS[mode]);
    modeField.setOnValueChange((value: string) => {
        modeField.setRectColour(MODE_COLOURS[value]);
    });
    return modeField;
}

function buildInputs(
    block: Blockly.Block,
    mode: string,
    board: 'orange' | 'blue',
    position: string = 'UPPER_LEFT',
    totalVar: string = '設定する変数',
    disconnect: boolean = true,
): void {
    if (disconnect) {
        if (block.previousConnection?.isConnected()) block.previousConnection.disconnect();
        if (block.nextConnection?.isConnected()) block.nextConnection.disconnect();
        if (block.outputConnection?.isConnected()) block.outputConnection.disconnect();
    }

    while (block.inputList.length > 0) {
        block.removeInput(block.inputList[0].name ?? '', true);
    }

    const positionOptions = POSITION_OPTIONS[board];
    const input = block.appendDummyInput('MAIN');
    input.appendField(new (Blockly.FieldImage as any)('/images/block/water_flow.svg', 65, 65, 'icon'));
    input.appendField(new FieldStyledLabel('水流センサー', { fontSize: 15, fontWeight: 'bold' }));
    input.appendField(new FieldStyledBadgeLabel('流量測定'));
    input.appendField(new FieldStyledLabel(' ', {}));
    input.appendField(new FieldOperatorDropdown(positionOptions, position, undefined, COLOR), 'POSITION');

    if (mode === 'PULSE_PER_SECOND') {
        block.setOutput(true, 'Number');
        block.setPreviousStatement(false);
        block.setNextStatement(false);
        (block as Blockly.BlockSvg).setOutputShape?.(2);
        input.appendField(new FieldStyledLabel('で', {}));
        input.appendField(buildModeField(mode), 'MODE');
        input.appendField(new FieldStyledLabel('を検知する ', {}));
    } else {
        block.setOutput(false);
        block.setPreviousStatement(true);
        block.setNextStatement(true);
        input.appendField(new FieldStyledLabel('で測定し、', {}));
        input.appendField(
            new FieldVariableLabel(totalVar, {
                fontWeight: 'normal',
                varType: 'float',
                colorOverride: CATEGORIES.variable.color,
            }),
            'TOTAL_VAR',
        );
        input.appendField(new FieldStyledLabel('に', {}));
        input.appendField(buildModeField(mode), 'MODE');
        input.appendField(new FieldStyledLabel('を入れる ', {}));
    }
}

defineMutableBlock({
    type: 'water_flow_measurement',
    colour: COLOR,
    tooltip: '水流センサーの流量を計測します',
    movable: true,
    deletable: true,

    init(this: Blockly.Block) {
        this.setColour(COLOR);
        this.setTooltip('水流センサーの流量を計測します');
        this.setMovable(true);
        this.setDeletable(true);
        (this as any)._mode = 'PULSE_PER_SECOND';
        (this as any)._board = 'orange';
        buildInputs(this, 'PULSE_PER_SECOND', 'orange', 'UPPER_LEFT', '設定する変数', false);
    },

    mutationToDom(this: Blockly.Block): Element {
        const container = Blockly.utils.xml.createElement('mutation');
        container.setAttribute('mode', (this as any)._mode ?? 'PULSE_PER_SECOND');
        container.setAttribute('board', (this as any)._board ?? 'orange');
        container.setAttribute('position', this.getFieldValue('POSITION') ?? 'UPPER_LEFT');
        container.setAttribute('total_var', this.getFieldValue('TOTAL_VAR') ?? '設定する変数');
        return container;
    },

    domToMutation(this: Blockly.Block, xml: Element): void {
        (this as any)._mode = xml.getAttribute('mode') ?? 'PULSE_PER_SECOND';
        (this as any)._board = xml.getAttribute('board') ?? 'orange';
        const position = xml.getAttribute('position') ?? 'UPPER_LEFT';
        const totalVar = xml.getAttribute('total_var') ?? '設定する変数';
        buildInputs(this, (this as any)._mode, (this as any)._board, position, totalVar, false);
    },

    onchange(this: Blockly.Block, e: Blockly.Events.Abstract) {
        if (!(e instanceof Blockly.Events.BlockChange)) return;
        if (e.blockId !== this.id) return;
        if (e.element !== 'field') return;
        if (e.name !== 'MODE') return;

        const newMode = e.newValue as string;
        if (newMode === (this as any)._mode) return;

        const savedPosition = this.getFieldValue('POSITION') ?? 'UPPER_LEFT';
        const savedVar = this.getFieldValue('TOTAL_VAR') ?? '設定する変数';
        const board = (this as any)._board ?? 'orange';

        (this as any)._mode = newMode;
        buildInputs(this, newMode, board, savedPosition, savedVar, true);
    },

    onBoardChange(block: Blockly.Block, board: 'orange' | 'blue') {
        (block as any)._board = board;
        const currentMode = (block as any)._mode ?? 'PULSE_PER_SECOND';
        const savedPosition = block.getFieldValue('POSITION') ?? 'UPPER_LEFT';
        const savedVar = block.getFieldValue('TOTAL_VAR') ?? '設定する変数';
        buildInputs(block, currentMode, board, savedPosition, savedVar, false);
    },
});
